import React, { useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Stack,
  Grid,
} from "@mui/material";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import {
  Code,
  ManageSearch,
  Campaign,
  Analytics,
  ArrowForward,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

export default function ServicesSct() {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);

  const services = [
    {
      title: "توسعه و مهندسی وب‌سایت",
      desc: "توسعه پلتفرم‌های سازمانی با معماری نرم‌افزاری مقیاس‌پذیر و عملکردی در تراز جهانی",
      icon: <Code />,
      color: "#00f2fe",
      gradient: "linear-gradient(135deg, #00f2fe, #4facfe)",
      number: "01",
    },
    {
      title: "بهینه‌سازی جستجو (SEO)",
      desc: "تسلط بر نتایج جستجو و هدایت پایدار ترافیک ارگانیک و باکیفیت به سمت سازمان شما",
      icon: <ManageSearch />,
      color: "#fa709a",
      gradient: "linear-gradient(135deg, #fa709a, #fee140)",
      number: "02",
    },
    {
      title: "راهبری شبکه‌های اجتماعی",
      desc: "مهندسی پیام و خلق کمپین‌های پربازده جهت تبدیل رسانه‌های شما به مرجع معتبر صنعت",
      icon: <Campaign />,
      color: "#7afcff",
      gradient: "linear-gradient(135deg, #7afcff, #4facfe)",
      number: "03",
    },
    {
      title: "تحلیل داده‌های کلان",
      desc: "هدایت استراتژی‌ها مبتنی بر داده‌های دقیق برای تضمین بالاترین نرخ بازگشت سرمایه",
      icon: <Analytics />,
      color: "#ffb86c",
      gradient: "linear-gradient(135deg, #ffb86c, #ff7b7b)",
      number: "04",
    },
  ];

  const setCardRef = (el, index) => {
    cardsRef.current[index] = el;
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        {
          y: 50,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
          },
        },
      );

      cardsRef.current?.forEach((card, index) => {
        gsap.fromTo(
          card,
          {
            opacity: 0,
            y: 70,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            delay: index * 0.12,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
            },
          },
        );
      });
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, []);

  const items = services.map((service, index) => (
    <Grid
      key={service.title}
      size={{ xs: 12, md: 6, xl: 3 }}
      sx={{
        p: 1.5,
      }}
    >
      <Card
        onClick={() => navigate("/services")}
        ref={(el) => setCardRef(el, index)}
        sx={{
          height: "100%",
          background: "rgba(18,18,30,0.75)",
          backdropFilter: "blur(12px)",
          borderRadius: "26px",
          border: "1px solid rgba(255,255,255,0.06)",
          transition: "0.35s",
          overflow: "hidden",
          position: "relative",
          cursor: "pointer",
          "&:hover": {
            transform: "translateY(-8px)",
            borderColor: service.color,
            boxShadow: `0 20px 45px ${service.color}20`,
          },

          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: `linear-gradient(135deg, ${service.color}10, transparent 60%)`,
            pointerEvents: "none",
          },
        }}
      >
        <CardContent
          sx={{
            p: 4,
            height: "100%",
          }}
        >
          <Stack
            spacing={3}
            sx={{
              height: "100%",
            }}
          >
            {/* Number */}
            <Typography
              sx={{
                fontSize: "2rem",
                fontWeight: 700,
                lineHeight: 1,
                background: service.gradient,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                opacity: 0.22,
              }}
            >
              {service.number}
            </Typography>

            {/* Icon */}
            <Avatar
              sx={{
                width: { xs: 48, md: 68 },
                height: { xs: 48, md: 68 },
                borderRadius: "18px",
                bgcolor: `${service.color}15`,
                color: service.color,
              }}
            >
              {React.cloneElement(service.icon, {
                sx: {
                  fontSize: { xs: 24, md: 34 },
                },
              })}
            </Avatar>

            {/* Content */}
            <Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 16, lg: 20 },
                  mb: 1.5,
                }}
              >
                {service.title}
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.55)",
                  fontSize: 13.5,
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                {service.desc}
              </Typography>

              {/* Action */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  color: service.color,
                  width: "fit-content",
                  transition: "0.3s",

                  "&:hover": {
                    transform: "translateX(-4px)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  بیشتر بدانید
                </Typography>

                <ArrowForward
                  sx={{
                    fontSize: 16,
                    rotate: "180deg",
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  ));

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 10, md: 14 },
        position: "relative",
        overflow: "hidden",

        "& *": {
          direction: "rtl",
        },
      }}
    >
      {/* Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "45px 45px",
          opacity: 0.25,
          pointerEvents: "none",
        }}
      />

      <Container
        maxWidth="xxl"
        sx={{
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Header */}
        <Stack
          spacing={2.5}
          alignItems="center"
          sx={{
            textAlign: "center",
            mb: 9,
          }}
        >
          <Typography
            ref={titleRef}
            component={"h1"}
            sx={{
              fontSize: { xs: 30, lg: 44 },
              fontWeight: 700,
              letterSpacing: "-0.03em",
              color: "secondary.main",
            }}
          >
            ویرا تک آروند آریا
          </Typography>

          <Typography
            sx={{
              maxWidth: 680,
              color: "rgba(255,255,255,0.58)",
              fontSize: "1rem",
              textAlign: "justify",

            }}
          >
            ویراتک آروند آریا یک شرکت فعال در حوزه فناوری و خدمات دیجیتال است که
            در زمینه طراحی و توسعه وب‌سایت، ساخت نرم‌افزارها و پلتفرم‌های
            اختصاصی، دیجیتال مارکتینگ، برندینگ و راهکارهای هوشمند مبتنی بر داده
            و هوش مصنوعی فعالیت می‌کند. <br /> ما با تکیه بر تجربه فنی و شناخت
            نیازهای واقعی کسب‌وکارها، راهکارهایی طراحی می‌کنیم که فقط یک حضور
            آنلاین نباشند؛ بلکه به رشد برند، بهبود تجربه مشتری، افزایش بهره‌وری
            و توسعه کسب‌وکار کمک کنند. <br /> از کسب‌وکارهای در حال شروع تا
            سازمان‌های بزرگ، ویرا در کنار شماست تا مسیر ایده تا اجرا و توسعه را
            با یک تیم تخصصی طی کنید.
          </Typography>
        </Stack>

        {/* Cards */}
        <Grid container justifyContent="center">
          {items}
        </Grid>
      </Container>
    </Box>
  );
}
